import { Property } from '../types/property';
import { filterZestimateDescriptions } from '../utils';
import { PropertyDetailPhotosParams } from '../types/search-params';
import { supabase } from '../supabase';
import { enhancedFetch } from '../utils/api-helpers';
import { transformDbPropertyToInterface } from '../utils/database-helpers';
import { getRentalUnits, getRentalPhotos } from '../utils/rental-database-helpers';
import { extractOpenHouseData } from '../utils/open-house-utils';

// Simple cache to prevent duplicate API calls
const propertyDetailsCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Guard to prevent multiple simultaneous calls to the same property
const activePropertyRequests = new Map<string, Promise<any>>();

/**
 * Get cached property details or fetch from API if not cached
 */
async function getCachedOrFetchPropertyDetails(propertyId: string): Promise<any> {
  const now = Date.now();
  
  // Check if we have an active request for this property
  if (activePropertyRequests.has(propertyId)) {
    return activePropertyRequests.get(propertyId);
  }

  // Check cache first
  const cached = propertyDetailsCache.get(propertyId);
  if (cached && now - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }

  // Create a new request promise
  const requestPromise = (async () => {
    try {
      const startTime = Date.now();
      
      const response = await enhancedFetch(
        `/api/properties/details?zpid=${propertyId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const duration = Date.now() - startTime;
      
      // Cache the successful response
      propertyDetailsCache.set(propertyId, {
        data,
        timestamp: now
      });
      
      return data;
    } catch (error) {
      throw error;
    } finally {
      // Clean up the active request
      activePropertyRequests.delete(propertyId);
    }
  })();

  // Store the active request
  activePropertyRequests.set(propertyId, requestPromise);
  
  return requestPromise;
}

/**
 * Transform rental property from database format to Property interface
 */
async function transformRentalPropertyToInterface(rentalProperty: any): Promise<Property> {
  // Fetch rental units to reconstruct unitsGroup data
  let unitsGroup = null;
  let photos: string[] = [];
  
  if (rentalProperty.id) {
    try {
      // Fetch units and photos in parallel
      const [units, rentalPhotos] = await Promise.all([
        getRentalUnits(rentalProperty.id),
        getRentalPhotos(rentalProperty.id)
      ]);
      
      if (units.length > 0) {
        unitsGroup = units.map(unit => ({
          bedrooms: unit.bedrooms || 0,
          bathrooms: unit.bathrooms || 1,
          squareFeet: unit.square_feet || null,
          minPrice: unit.price || 0,
          maxPrice: unit.price || 0,
          isRoomForRent: unit.is_room_for_rent || false
        }));
      }
      
      if (rentalPhotos.length > 0) {
        photos = rentalPhotos;
      }
    } catch (error) {
      // Could not fetch rental data
    }
  }
  
  // Fallback to stored photos if no rental photos found
  if (photos.length === 0) {
    photos = rentalProperty.photos || [rentalProperty.primary_photo_url].filter(Boolean);
  }
  
  // Extract rental metadata from the database
  const rentalMetadata = {
    areApplicationsAccepted: rentalProperty.are_applications_accepted || false,
    isRoomForRent: rentalProperty.is_room_for_rent || false,
    isBuildToRent: rentalProperty.is_build_to_rent || false,
    isFeatured: rentalProperty.is_featured || false,
    isShowcaseListing: rentalProperty.is_showcase_listing || false,
    matchingHomeCount: rentalProperty.matching_home_count || 0,
    providerListingID: rentalProperty.provider_listing_id || '',
    currency: rentalProperty.currency || 'usd',
    country: rentalProperty.country || 'usa',
    groupType: rentalProperty.group_type || 'apartment',
    listingDateTimeOnZillow: rentalProperty.listing_datetime_on_zillow || null,
    bestGuessTimeZone: rentalProperty.best_guess_timezone || null,
    marketingTreatments: rentalProperty.marketing_treatments || [],
    // Store reconstructed unitsGroup
    unitsGroup: unitsGroup
  };
  
  // Create the Property object
  const property: Property = {
    id: rentalProperty.id || rentalProperty.zpid,
    title: rentalProperty.title || 'Rental Property',
    address: rentalProperty.address_street || '',
    city: rentalProperty.city || '',
    state: rentalProperty.state || '',
    zipCode: rentalProperty.zipcode || '',
    price: rentalProperty.min_price || rentalProperty.max_price || 0,
    details: {
      bedrooms: rentalProperty.bedrooms || 0,
      bathrooms: rentalProperty.bathrooms || 0,
      squareFeet: rentalProperty.square_feet || 0,
      yearBuilt: rentalProperty.year_built || 0,
      lotSize: { amount: rentalProperty.lot_size || 0 },
    },
    type: rentalProperty.home_type || rentalProperty.group_type || 'apartment',
    status: 'forRent',
    photos: photos,
    description: rentalProperty.description || 'Rental property',
    location: {
      lat: rentalProperty.latitude || 0,
      lon: rentalProperty.longitude || 0,
    },
    url: rentalProperty.posting_url || `https://www.zillow.com/rental/${rentalProperty.zpid}`,
    last_sync: rentalProperty.last_sync || new Date().toISOString(),
    source: 'zillow',
    source_id: String(rentalProperty.zpid),
    
  };
  
  // Add rental metadata
  (property as any).rentalMetadata = rentalMetadata;
  
  // Add price range if available
  if (rentalProperty.min_price && rentalProperty.max_price && rentalProperty.min_price !== rentalProperty.max_price) {
    (property as any).priceRange = {
      min: rentalProperty.min_price,
      max: rentalProperty.max_price,
      display: `$${rentalProperty.min_price.toLocaleString()} - $${rentalProperty.max_price.toLocaleString()}`
    };
  }
  
  return property;
}

/**
 * Update property photos in the database
 */
async function updatePropertyPhotos(propertyId: string, photos: string[], imageUrl: string): Promise<void> {
  const { error } = await supabase
    .from('properties')
    .update({
      photos: photos,
      imageUrl: imageUrl,
      last_sync: new Date().toISOString()
    })
    .eq('source_id', propertyId);

  if (error) {
    // Error updating property photos
  }
}

/**
 * Process properties in batches with a common pattern
 */
async function processPropertiesInBatches<T>(
  properties: any[],
  processor: (property: any) => Promise<T>,
  context: string,
  limit: number = 50
): Promise<{ successCount: number; failureCount: number }> {
  let successCount = 0;
  let failureCount = 0;

  for (const property of properties.slice(0, limit)) {
    try {
      const result = await processor(property);
      if (result) {
        successCount++;
      } else {
        failureCount++;
      }
      
      // Add a small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (error) {
      failureCount++;
    }
  }



  return { successCount, failureCount };
}

/**
 * Log the number of properties found for processing
 */


/**
 * Log the start of an operation
 */


/**
 * Create a property object from Zillow API response
 */
function createPropertyFromZillowResponse(prop: any, propertyId: string): Property {
  // Helper function to extract data from Zillow's quickFacts array
  const extractFromQuickFacts = (elementType: string): number => {
    if (!prop.quickFacts || !Array.isArray(prop.quickFacts)) {
      return 0;
    }
    
    const fact = prop.quickFacts.find((fact: any) => fact.elementType === elementType);
    if (fact && fact.value) {
      // Try different value structures
      let valueStr = '';
      
      if (fact.value.fullValue) {
        valueStr = fact.value.fullValue;
      } else if (fact.value.abbreviatedValue) {
        valueStr = fact.value.abbreviatedValue;
      } else if (typeof fact.value === 'string') {
        valueStr = fact.value;
      } else if (typeof fact.value === 'number') {
        return fact.value;
      }
      
      if (valueStr) {
        // Remove commas and extract number
        const cleanValue = valueStr.replace(/,/g, '').replace(/[^\d]/g, '');
        const value = parseInt(cleanValue);
        return isNaN(value) ? 0 : value;
      }
    }
    
    return 0;
  };

  // Enhanced square footage extraction
  const getSquareFeet = (): number => {
    const quickFactsSqft = extractFromQuickFacts('livingArea');
    if (quickFactsSqft > 0) return quickFactsSqft;
    
    const directSqft = prop.squareFeet || 
                     prop.livingArea || 
                     prop.homeSize || 
                     prop.size ||
                     prop.area ||
                     prop.sqft ||
                     prop.livingAreaValue ||
                     0;
    
    if (directSqft > 0) return directSqft;
    
    if (prop.resoFacts?.livingArea) {
      const resoSqft = parseInt(prop.resoFacts.livingArea);
      if (!isNaN(resoSqft) && resoSqft > 0) return resoSqft;
    }
    
    return 0;
  };

  // Enhanced lot size extraction
  const getLotSize = (): number => {
    const quickFactsLotSize = extractFromQuickFacts('lotSize');
    if (quickFactsLotSize > 0) return quickFactsLotSize;
    
    const directLotSize = prop.lotSize || 
                         prop.lotSizeValue || 
                         prop.lot ||
                         prop.lotAreaValue ||
                         prop.lotArea ||
                         prop.lot_size ||
                         0;
    
    if (directLotSize > 0) return directLotSize;
    
    if (prop.resoFacts?.lotSize) {
      const resoLotSize = parseInt(prop.resoFacts.lotSize);
      if (!isNaN(resoLotSize) && resoLotSize > 0) return resoLotSize;
    }
    
    return 0;
  };

  return {
    title: prop.title || 'Property',
    address: prop.address || '',
    city: prop.city || '',
    state: prop.state || '',
    zipCode: prop.zipCode || '',
    price: typeof prop.price === 'number' ? prop.price : Number(prop.price) || 0,
    type: prop.homeType?.toLowerCase() || 'apartment',
    details: {
      bedrooms: prop.bedrooms || extractFromQuickFacts('beds') || 1,
      bathrooms: prop.bathrooms || extractFromQuickFacts('baths') || 1,
      squareFeet: getSquareFeet(),
      yearBuilt: prop.yearBuilt || 0,
      lotSize: { amount: getLotSize() },
    },
    location: {
      lat: prop.latitude || 0,
      lon: prop.longitude || 0,
    },
    imageUrl: prop.imageUrl || '',
    photos: prop.photos || [],
    description: filterZestimateDescriptions(extractPropertyDescription(prop)),
    status: 'forRent',
    source: 'zillow',
    source_id: propertyId,
    last_sync: new Date().toISOString(),
    hoa_dues: extractPropertyHoaData(prop),
    openHouses: extractOpenHouseData(prop),
    
    url: prop.postingUrl || `https://www.zillow.com/rental/${propertyId}`,
  };
}

/**
 * Get detailed property information by property ID
 * @param propertyId - The property ID to fetch details for
 * @returns Promise<Property | null> - Property details or null if not found
 */
export async function getPropertyDetails(propertyId: string): Promise<Property | null> {
  try {
    
    // First try to fetch from rental_properties table (for rental properties)
    let { data: rentalProperty, error: rentalError } = await supabase
      .from('rental_properties')
      .select('*')
      .eq('zpid', propertyId)
      .maybeSingle();
    
    // If we found a rental property, return it
    if (rentalProperty && !rentalError) {
      return await transformRentalPropertyToInterface(rentalProperty);
    }
    
    // If no rental property found, try the regular properties table
    let { data: property, error } = await supabase
      .from('properties')
      .select('*')
      .eq('source_id', propertyId)
      .maybeSingle();
    
    // If we found a property, return it
    if (property && !error) {
      return await transformDbPropertyToInterface(property);
    }
    
    // If both database lookups fail, try to fetch from Zillow API
    if (!property && !rentalProperty) {
      
      try {
        const result = await getUnifiedPropertyData(propertyId);
        
        if (!result.success || !result.property) {
          return null;
        }
        
        const prop = result.property;
        
        // Create a simple property object from the API response
        const propertyFromZillow: Property = createPropertyFromZillowResponse(prop, propertyId);
        
        // Note: No caching here - this function should only fetch and return data
        // Caching is handled elsewhere to prevent duplicate upserts
        
        return propertyFromZillow;
      } catch (error) {
        return null;
      }
    }
    
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Test function to update a property's Matterport URL
 * @param propertyId - The property ID to update
 * @param matterportUrl - The Matterport URL to set
 */
export async function testUpdateMatterportUrl(propertyId: string, matterportUrl: string): Promise<void> {
  try {


    const { data, error } = await supabase
      .from('properties')
      .update({ 
        last_sync: new Date().toISOString()
      })
      .eq('source_id', propertyId);

    if (error) {
      // Error updating Matterport URL
    }
  } catch (error) {
    // Unexpected error updating Matterport URL
  }
}

/**
 * Refresh missing Matterport URLs for properties that have virtual tours but no Matterport URL
 */
export async function refreshMissingMatterportUrls(): Promise<void> {
  try {
  

    const { data: properties, error } = await supabase
      .from('properties')
      .select('*')
      .limit(0);

    if (error) {
      return;
    }

  

    await processPropertiesInBatches(
      properties,
      async (property) => {


        return false;
      },
      'Refresh Missing Matterport URLs'
    );
  } catch (error) {
    // Error refreshing missing Matterport URLs
  }
}

/**
 * Test function to update a property's description
 * @param propertyId - The property ID to update
 * @param description - The description to set
 */
export async function testUpdateDescription(propertyId: string, description: string): Promise<void> {
  try {


    const { data, error } = await supabase
      .from('properties')
      .update({ 
        description: description,
        last_sync: new Date().toISOString()
      })
      .eq('source_id', propertyId);

    if (error) {
      // Error updating description
    }
  } catch (error) {
    // Unexpected error updating description
  }
}

/**
 * Refresh missing descriptions for properties that don't have descriptions
 */
export async function refreshMissingDescriptions(): Promise<void> {
  try {
  

    // Get properties that don't have descriptions or have empty descriptions
    const { data: properties, error } = await supabase
      .from('properties')
      .select('*')
      .or('description.is.null,description.eq.');

    if (error) {
      return;
    }

  

    await processPropertiesInBatches(
      properties,
      async (property) => {

        
        const response = await rateLimitedFetch(
          `/api/properties/details?zpid=${property.source_id}`,
          {
            method: 'GET',
          }
        );

        if (response.ok) {
          const data = await response.json();
          

          
          // Use the helper function to extract description
          let description = '';
          
          // Check in the main data object first
          description = extractPropertyDescription(data);
          
          // If not found in main data, check in data.data
          if (!description && data?.data) {
            description = extractPropertyDescription(data.data);
          }
          

          
          if (description && description.trim() !== '') {
            await testUpdateDescription(property.source_id, description);
            return true;
          } else {
            // Set default description if none found
            await testUpdateDescription(property.source_id, 'No description available');
            return false;
          }
        } else {
          return false;
        }
      },
      'Refresh Missing Descriptions'
    );
  } catch (error) {
    // Error refreshing missing descriptions
  }
}

/**
 * Refresh photos for a specific coming soon property using URL-based endpoints
 * @param propertyId - The property ID to refresh photos for
 * @returns Promise<Property | null> - Updated property with photos or null if not found
 */
export async function refreshComingSoonPropertyPhotos(propertyId: string): Promise<Property | null> {
  try {
    // First, get the property from the database
    const { data: property, error } = await supabase
      .from('properties')
      .select('*')
      .eq('source_id', propertyId)
      .single();

    if (error || !property) {
      return null;
    }

    // Check if it's a coming soon property
    if (property.status !== 'Coming Soon') {
      return await transformDbPropertyToInterface(property);
    }

    // Check if it already has photos
    if (property.photos && property.photos.length > 0) {
      return await transformDbPropertyToInterface(property);
    }

    // Check if it has a URL
    if (!property.url) {
      return await transformDbPropertyToInterface(property);
    }

    // Import the enhanced photo fetching function
    const { fetchPhotosForComingSoonProperty } = await import('../utils/photo-extraction');
    const { rateLimitedFetch } = await import('../utils/api-helpers');

    // Try to fetch property details using URL-based endpoints
    const updatedProperty = await fetchPhotosForComingSoonProperty(property, rateLimitedFetch);

    if (updatedProperty.photos && updatedProperty.photos.length > 0) {
      // Update the property in the database with the new photos
      await updatePropertyPhotos(propertyId, updatedProperty.photos, updatedProperty.imageUrl);
      return await transformDbPropertyToInterface(updatedProperty);
    } else {
      // If URL-based fetching didn't work, try the property details endpoint
      try {
        const result = await getUnifiedPropertyData(propertyId);
        
        if (result.success && result.property?.photos) {
          const photos = result.property.photos;
          const imageUrl = photos[0] || '';
          
          if (photos.length > 0) {
            // Update the property in the database with the new photos
            await updatePropertyPhotos(propertyId, photos, imageUrl);
            return await transformDbPropertyToInterface({
              ...property,
              photos,
              imageUrl
            });
          }
        }
      } catch (error) {
        // Error handling removed for cleaner code
      }
      return await transformDbPropertyToInterface(property);
    }

  } catch (error) {
    // Error handling removed for cleaner code
    return null;
  }
}


/**
 * Get property detail photos by URL
 * @param params - PropertyDetailPhotosParams object
 * @returns Promise<any> - Property detail photos
 */
export async function getPropertyDetailPhotos(params: PropertyDetailPhotosParams): Promise<any> {
  try {
    // Zillow API doesn't support URL-based photo fetching
    // We'll use the property details endpoint instead
    const response = await rateLimitedFetch(
      `/api/properties/details?zpid=${params.url}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    if (!response.ok) {
      throw new Error(`Property detail photos API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    return data;
  } catch (error) {
    throw error;
  }
}

/**
 * Get property photos by URL
 * @param propertyUrl - The property URL to fetch photos for
 * @returns Promise<any> - Property photos
 */
export async function getPropertyPhotosByUrl(propertyUrl: string): Promise<any> {
  try {
    // Zillow API doesn't support URL-based photo fetching
    // We'll use the property details endpoint instead
    const response = await rateLimitedFetch(
      `/api/properties/details?zpid=${propertyUrl}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    if (!response.ok) {
      throw new Error(`Property photos API error: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
} 

/**
 * Manually update description for a specific property
 * @param propertyId - The property ID to update
 * @returns Promise<boolean> - True if update was successful
 */
export async function updatePropertyDescription(propertyId: string): Promise<boolean> {
  try {


    const result = await getUnifiedPropertyData(propertyId);
    
    if (!result.success || !result.property) {
      return false;
    }
    
    const description = extractPropertyDescription(result.property);
    

    
    if (description && description.trim() !== '') {
      await testUpdateDescription(propertyId, description);
      return true;
    } else {
      await testUpdateDescription(propertyId, 'No description available');
      return false;
    }
  } catch (error) {
    return false;
  }
}

/**
 * Update HOA data for a specific property
 * @param propertyId - The property ID to update
 * @returns Promise<boolean> - True if update was successful
 */
export async function updatePropertyHoaData(propertyId: string): Promise<boolean> {
  try {


    const result = await getUnifiedPropertyData(propertyId);
    
    if (!result.success || !result.property) {
      return false;
    }
    
    const hoaData = extractPropertyHoaData(result.property);
    

    
    if (hoaData) {
      // Update the property in the database
      const { error } = await supabase
        .from('properties')
        .update({ 
          hoa_dues: hoaData,
          last_sync: new Date().toISOString()
        })
        .eq('source_id', propertyId);

      if (error) {
        return false;
      }
      
      return true;
    } else {
      return false;
      }
  } catch (error) {
    return false;
  }
}

/**
 * Update HOA data for all properties in the database
 * @param limit - Maximum number of properties to update (default: 50)
 * @returns Promise<void>
 */
export async function updateAllPropertyHoaData(limit: number = 50): Promise<void> {
  try {
  

    // Get all properties from the database
    const { data: properties, error } = await supabase
      .from('properties')
      .select('*')
      .limit(limit);

    if (error) {
      return;
    }

  

    await processPropertiesInBatches(
      properties,
      async (property) => await updatePropertyHoaData(property.source_id),
      'Update All Property HOA Data',
      limit
    );
  } catch (error) {
    // Error updating all property HOA data
  }
}

/**
 * Update descriptions for all properties in the database
 * @param limit - Maximum number of properties to update (default: 50)
 * @returns Promise<void>
 */
export async function updateAllPropertyDescriptions(limit: number = 50): Promise<void> {
  try {
  

    // Get all properties from the database
    const { data: properties, error } = await supabase
      .from('properties')
      .select('*')
      .limit(limit);

    if (error) {
      return;
    }

  

    await processPropertiesInBatches(
      properties,
      async (property) => await updatePropertyDescription(property.source_id),
      'Update All Property Descriptions',
      limit
    );
  } catch (error) {
    // Error updating all property descriptions
  }
} 

/**
 * Unified function to get property data from API with caching
 * This prevents duplicate API calls across different functions
 */
async function getUnifiedPropertyData(propertyId: string): Promise<{
  success: boolean;
  data?: any;
  property?: any;
  error?: string;
}> {
  try {
    
    const zillowData = await getCachedOrFetchPropertyDetails(propertyId);
    
    // Check if data is nested under 'data' or at the top level
    let prop = zillowData.data;
    if (!prop && zillowData.zpid) {
      prop = zillowData;
    }
    
    if (!prop) {
      return { success: false, error: 'No property data found in API response' };
    }
    
    return { 
      success: true, 
      data: zillowData, 
      property: prop 
    };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Extract description from property data
 */
function extractPropertyDescription(prop: any): string {
  if (prop.description) return prop.description;
  if (prop.propertyDescription) return prop.propertyDescription;
  if (prop.listingDescription) return prop.listingDescription;
  if (prop.homeDescription) return prop.homeDescription;
  if (prop.propertyDetails?.description) return prop.propertyDetails.description;
  if (prop.listingDetails?.description) return prop.listingDetails.description;
  return '';
}

/**
 * Extract HOA data from property data
 */
function extractPropertyHoaData(prop: any): any {
  if (!prop.hoaFee && !prop.monthlyHoaFee) return null;
  
  const hoaData = {
    amount: prop.hoaFee || 0,
    frequency: prop.hoaFeeFrequency || 'Annually',
    monthlyAmount: prop.monthlyHoaFee || 0,
    hasHoa: true
  };
  
  // If we have monthly amount but no annual amount, calculate it
  if (hoaData.monthlyAmount > 0 && hoaData.amount === 0) {
    hoaData.amount = hoaData.monthlyAmount * 12;
  }
  
  // If we have annual amount but no monthly amount, calculate it
  if (hoaData.amount > 0 && hoaData.monthlyAmount === 0) {
    hoaData.monthlyAmount = Math.round(hoaData.amount / 12);
  }
  
  return hoaData;
} 